import { ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import ProjectTimeReportCard from 'src/components/ProjectTimeReportCard';
import useProjectAccess from 'src/hooks/useProjectAccess';
import SmsSignUpModal from 'src/components/SmsSignUpModal';
import InviteModal from 'src/components/InviteModal';
import ResetPasswordModal from 'src/components/ResetPasswordModal';
import TutorialModal from 'src/components/TutorialModal';
import AppHeader from 'src/components/AppHeader';
import { getPendingInvites } from 'src/store/pendingInvites/reducer';
import { getContacts } from 'src/store/contacts/reducer';
import { getInbox } from 'src/store/inbox/reducer';
import ENVIRONMENT from 'src/utils/environments';
import useWebSocket from 'src/utils/useWebSocket';
import { UserResponse } from 'src/types';
import Navbar from './Navbar';
import MenuLeft from './MenuLeft';
import OnboardingToast from '../OnboardingToast';
import ClientSurveyToast from '../ClientSurveyToast';
import ClientSurveyModal from '../ClientSurveyModal';
import { RootState } from 'src/store';
import { getCurrentCustomer } from 'src/utils/localStorage';
import { LOCAL_STORAGE_CHECK_ONBOARDING_KEY } from 'src/utils/constants';
import { IoCartOutline } from 'react-icons/io5';

const version = process.env.REACT_APP_GIT_HASH
  ? process.env.REACT_APP_GIT_HASH.substring(0, 6)
  : null;

const linkClassName = 'flex items-center gap-2 font-semibold';

export default function Layout({ children }: { children?: ReactNode }) {
  const [resetModal, setResetModal] = useState(false);
  const [inviteModal, setInviteModal] = useState(false);
  const [surveyModalOpen, setSurveyModalOpen] = useState(false);
  const [surveyToastDismissed, setSurveyToastDismissed] = useState(false);

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const user: UserResponse = useSelector(
    (globalState: any) => globalState.auth.user
  );

  const project = useSelector(
    (globalState: RootState) => globalState.projectRetainer.data
  );

  const { canSeeCredits } = useProjectAccess();
  const hasCompleteOnboarding =
    project?.customer?.onboarding_step === 1 && getCurrentCustomer();

  const breakpoints = useBreakpoint(true);

  const dispatch = useDispatch();

  const dashboardPage = pathname === '/dashboard';

  const inboxSyncRef = useRef<number>();

  useEffect(() => {
    dispatch(getInbox());
  }, [dispatch]);

  // Nothing else refreshes the mentions badge while the app is open: the count is derived from
  // the inbox list, and the list is only fetched here and on the inbox screen. Without this the
  // badge stays at the value it had on page load until the user happens to visit the inbox.
  useWebSocket({
    channelName: `users.${user?.uuid}`,
    enabled: !!user?.uuid,
    listen: {
      event: '.mention.change',
      callback: () => {
        // One action can flip several mentions at once - opening a thread, mark all as read -
        // and each one arrives as its own event, so collapse a burst into a single refetch.
        window.clearTimeout(inboxSyncRef.current);

        inboxSyncRef.current = window.setTimeout(() => {
          dispatch(getInbox());
        }, 300);
      },
    },
  });

  useEffect(() => {
    return () => window.clearTimeout(inboxSyncRef.current);
  }, []);

  useEffect(() => {
    const checkOnboardingFlag = localStorage.getItem(
      LOCAL_STORAGE_CHECK_ONBOARDING_KEY
    );

    if (
      checkOnboardingFlag &&
      user &&
      !hasCompleteOnboarding &&
      pathname !== '/onboarding'
    ) {
      navigate('/onboarding', { replace: true });

      localStorage.removeItem(LOCAL_STORAGE_CHECK_ONBOARDING_KEY);
    } else if (checkOnboardingFlag === 'true' && !hasCompleteOnboarding) {
      localStorage.removeItem(LOCAL_STORAGE_CHECK_ONBOARDING_KEY);
    }
  }, [user, hasCompleteOnboarding, pathname, navigate]);

  return (
    <div className="relative flex min-h-[100vh] flex-col">
      <AppHeader />

      <ResetPasswordModal
        visible={resetModal}
        onClose={() => setResetModal(false)}
      />

      <InviteModal
        visible={inviteModal}
        onInviteSent={() => {
          dispatch(getContacts());
          dispatch(getPendingInvites());
        }}
        onClose={() => setInviteModal(false)}
      />

      <TutorialModal />

      {user.preferences.sms_notifications === null ? (
        <SmsSignUpModal user={user} />
      ) : undefined}

      <Navbar
        onInvite={() => setInviteModal(true)}
        onResetPassword={() => setResetModal(true)}
      />

      <div className="mx-auto w-full max-w-[1920px] flex-1 px-2 pt-2 sm:px-6 sm:pt-4 xl:pt-0">
        <div className="flex gap-4 max-[1280px]:flex-wrap xl:gap-6">
          {breakpoints.xl ? (
            <div className="relative w-[280px]">
              <div
                className={`sticky top-0 flex flex-col justify-between ${
                  ENVIRONMENT.IS_PRODUCTION
                    ? `top-16 min-h-[calc(100vh-75px)]`
                    : `top-[92px] min-h-[calc(100vh-105px)]`
                }`}
              >
                <div>
                  <MenuLeft />

                  {canSeeCredits && (
                      <>
                        <div className="w-full p-2 pt-4">
                          <ProjectTimeReportCard showTooltip />
                        </div>

                        <Link
                          to="/checkout"
                          className={`${linkClassName} rounded-lg bg-[#0044CC] bg-opacity-10 py-2 
          transition-colors duration-200 hover:bg-[#0044CC] hover:bg-opacity-20 hover:text-blue-700`}
                        >
                          <IoCartOutline className="mx-4 text-lg text-blue-600" />
                          <span className="text-blue-600">Buy more hours</span>
                        </Link>
                      </>
                    )
                  }
                </div>

                <div className={`hidden pt-2 text-xs text-gray-300 xl:block`}>
                  {version ? `Ver: ${version}` : undefined}
                </div>
              </div>
            </div>
          ) : (
            dashboardPage && canSeeCredits && (
              <div className="w-full">
                <ProjectTimeReportCard showTooltip />
              </div>
            )
          )}

          <div className="w-full flex-1 basis-full pb-6 xl:order-none xl:basis-0 xl:pb-16 xl:pt-4">
            {children}
          </div>
        </div>
      </div>

      <div className="absolute bottom-1 left-6 text-xs text-gray-300 xl:hidden">
        {version ? `Ver: ${version}` : undefined}
      </div>

      {user.survey_due && !surveyToastDismissed && (
        <ClientSurveyToast
          onOpen={() => setSurveyModalOpen(true)}
          hasLowerToast={hasCompleteOnboarding === false}
        />
      )}
      <OnboardingToast hasComplete={hasCompleteOnboarding} />
      <ClientSurveyModal
        open={surveyModalOpen}
        onClose={() => setSurveyModalOpen(false)}
        onComplete={() => {
          setSurveyToastDismissed(true);
          setSurveyModalOpen(false);
        }}
      />
    </div>
  );
}
